import React, { Component } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";

import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";
import DeleteIcon from "@material-ui/icons/Delete";

import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const COLUMN_LABELS = {
  ean: "EAN",
  brand: "Brand",
  pattern: "Pattern",
  size: "Size",
  quantity: "Quantity",
  remain: "Remain",
  price: "Price",
  source: "Source",
  total: "Total",
  priceIls: "Price in ILS",
  dot: "DOT",
  notes: "Notes",
};

const HELP_STEPS = [
  "ב-Google Cloud Console בחרו או צרו פרויקט (למשל sportyshop-sheets).",
  "APIs & Services → Library → Google Sheets API → Enable.",
  "IAM & Admin → Service Accounts → Create service account (למשל sportyshop-sheet-sync). אין צורך בתפקידים בפרויקט.",
  "פתחו את חשבון השירות → Keys → Add key → Create new key → JSON. יורד קובץ .json.",
  "במסך הזה העלו או הדביקו את ה-JSON ושמרו. המסך יציג את כתובת הדוא״ל של חשבון השירות.",
  "פתחו את גיליון ההזמנה → Share → הוסיפו את הכתובת בהרשאת Editor, ובטלו את הסימון Notify.",
  "הדביקו את קישור הגיליון, שמרו, ולחצו על \"בדוק חיבור והרשאת כתיבה\".",
  "לכל מכולה חדשה (גיליון חדש): שתפו אותו עם אותה כתובת, ואז עדכנו את הקישור במסך הזה.",
];

const styles = (theme) => ({
  root: {
    padding: theme.spacing(1),
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  row: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    marginTop: theme.spacing(1),
  },
  alert: {
    padding: "10px 12px",
    borderRadius: 6,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  info: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    marginTop: theme.spacing(1),
    lineHeight: 1.5,
  },
  field: {
    marginTop: theme.spacing(2),
  },
  actions: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginTop: theme.spacing(2),
  },
  success: {
    color: "#2e7d32",
  },
  error: {
    color: "#c62828",
  },
  step: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginTop: 8,
  },
  helpList: {
    margin: 0,
    paddingRight: 18,
    lineHeight: 1.7,
  },
});

function nextRowId() {
  return `row-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formFromConfig(config) {
  const source = config || {};
  const labels = source.totalsMarkerLabels && source.totalsMarkerLabels.length
    ? source.totalsMarkerLabels
    : ["Units", "Total net", "MwSt", "Shipment", "Total cost"];
  const map = source.supplierSourceMap && source.supplierSourceMap.length
    ? source.supplierSourceMap
    : [{ match: "rduch", source: "RDUCH" }, { match: "dsk", source: "DSK" }];
  return {
    sheetUrl: source.sheetUrl || "",
    sheetTitleOverride: source.sheetTitleOverride || "",
    notesColumnHeader: source.notesColumnHeader || "",
    totalsMarkerLabels: labels.join(", "),
    supplierSourceMap: map.map((row) => ({
      id: nextRowId(),
      match: row.match || "",
      source: row.source || "",
    })),
    serviceAccountJson: "",
  };
}

function formSnapshot(form) {
  return JSON.stringify({
    sheetUrl: form.sheetUrl,
    sheetTitleOverride: form.sheetTitleOverride,
    notesColumnHeader: form.notesColumnHeader,
    totalsMarkerLabels: form.totalsMarkerLabels,
    supplierSourceMap: (form.supplierSourceMap || []).map((row) => ({
      match: row.match,
      source: row.source,
    })),
    serviceAccountJson: form.serviceAccountJson || "",
  });
}

function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("he-IL");
}

function stepIcon(status) {
  if (status === "ok") return <CheckCircleIcon style={{ color: "#16a34a" }} />;
  if (status === "error") return <ErrorIcon style={{ color: "#dc2626" }} />;
  if (status === "warning") return <WarningIcon style={{ color: "#d97706" }} />;
  return <span style={{ width: 24, textAlign: "center", color: "#6b7280" }}>–</span>;
}

class GoogleSheetsSyncManager extends Component {
  constructor(props) {
    super(props);
    const form = formFromConfig(null);
    this.state = {
      loading: true,
      savingCredentials: false,
      savingSettings: false,
      testing: false,
      config: null,
      error: null,
      credentialsMessage: null,
      credentialsError: null,
      settingsMessage: null,
      settingsError: null,
      testResult: null,
      helpOpen: false,
      copied: false,
      savedSnapshot: formSnapshot(form),
      ...form,
    };
  }

  componentDidMount() {
    this.fetchConfig();
  }

  fetchConfig = async ({ quiet } = {}) => {
    if (!quiet) this.setState({ loading: true, error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/google-sheets-sync`, {
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to load");
      if (quiet) {
        this.setState({ config: data.config });
        return;
      }
      const form = formFromConfig(data.config);
      this.setState({
        loading: false,
        config: data.config,
        ...form,
        savedSnapshot: formSnapshot(form),
      });
    } catch (err) {
      this.setState({ loading: false, error: quiet ? this.state.error : err.message });
    }
  };

  applySavedConfig = (config, extra = {}) => {
    const { keepServiceAccountJson, ...rest } = extra;
    const form = formFromConfig(config);
    const pendingJson = keepServiceAccountJson ? this.state.serviceAccountJson : "";
    this.setState({
      config,
      ...form,
      serviceAccountJson: pendingJson,
      savedSnapshot: formSnapshot(form),
      ...rest,
    });
  };

  handleField = (field) => (event) => {
    this.setState({
      [field]: event.target.value,
      settingsMessage: null,
      settingsError: null,
      credentialsMessage: null,
      credentialsError: null,
    });
  };

  handleJsonFile = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.setState({
        serviceAccountJson: String(reader.result || ""),
        credentialsMessage: null,
        credentialsError: null,
      });
    };
    reader.readAsText(file);
  };

  handleCopyEmail = () => {
    const email = this.state.config && this.state.config.serviceAccount && this.state.config.serviceAccount.clientEmail;
    if (!email) return;
    const done = () => this.setState({ copied: true }, () => {
      setTimeout(() => this.setState({ copied: false }), 1500);
    });
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(email).then(done).catch(done);
      return;
    }
    done();
  };

  handleSaveCredentials = async () => {
    const json = (this.state.serviceAccountJson || "").trim();
    if (!json) {
      this.setState({ credentialsError: "יש להדביק את קובץ ה-JSON.", credentialsMessage: null });
      return;
    }
    this.setState({ savingCredentials: true, credentialsError: null, credentialsMessage: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/google-sheets-sync`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
        body: JSON.stringify({ serviceAccountJson: json }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to save");
      this.applySavedConfig(data.config, {
        savingCredentials: false,
        credentialsMessage: "פרטי הגישה נשמרו",
        testResult: null,
      });
    } catch (err) {
      this.setState({ savingCredentials: false, credentialsError: err.message });
    }
  };

  handleDeleteCredentials = async () => {
    if (!window.confirm("למחוק את פרטי חשבון השירות?")) return;
    this.setState({ savingCredentials: true, credentialsError: null, credentialsMessage: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/google-sheets-sync/credentials`, {
        method: "DELETE",
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to delete");
      this.applySavedConfig(data.config, {
        savingCredentials: false,
        credentialsMessage: "פרטי הגישה נמחקו",
        testResult: null,
      });
    } catch (err) {
      this.setState({ savingCredentials: false, credentialsError: err.message });
    }
  };

  handleMapChange = (id, field) => (event) => {
    const value = event.target.value;
    this.setState((prev) => ({
      supplierSourceMap: prev.supplierSourceMap.map((row) => (
        row.id === id ? { ...row, [field]: value } : row
      )),
      settingsMessage: null,
      settingsError: null,
    }));
  };

  handleAddMapRow = () => {
    this.setState((prev) => ({
      supplierSourceMap: prev.supplierSourceMap.concat([{ id: nextRowId(), match: "", source: "" }]),
      settingsMessage: null,
    }));
  };

  handleDeleteMapRow = (id) => {
    this.setState((prev) => ({
      supplierSourceMap: prev.supplierSourceMap.filter((row) => row.id !== id),
      settingsMessage: null,
    }));
  };

  handleSaveSettings = async () => {
    this.setState({ savingSettings: true, settingsError: null, settingsMessage: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/google-sheets-sync`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
        body: JSON.stringify({
          sheetUrl: this.state.sheetUrl,
          sheetTitleOverride: this.state.sheetTitleOverride,
          notesColumnHeader: this.state.notesColumnHeader,
          totalsMarkerLabels: this.state.totalsMarkerLabels,
          supplierSourceMap: this.state.supplierSourceMap
            .filter((row) => row.match || row.source)
            .map((row) => ({ match: row.match, source: row.source })),
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to save");
      this.applySavedConfig(data.config, {
        savingSettings: false,
        settingsMessage: "ההגדרות נשמרו",
        testResult: null,
        keepServiceAccountJson: true,
      });
    } catch (err) {
      this.setState({ savingSettings: false, settingsError: err.message });
    }
  };

  handleTest = async () => {
    this.setState({ testing: true, testResult: null, settingsError: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/google-sheets-sync/test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
      });
      const data = await res.json();
      if (!res.ok && !data.steps) throw new Error(data.error || "Failed to test");
      this.setState({ testing: false, testResult: data });
      this.fetchConfig({ quiet: true });
    } catch (err) {
      this.setState({ testing: false, settingsError: err.message });
    }
  };

  renderAccount(classes) {
    const { config, copied } = this.state;
    const account = (config && config.serviceAccount) || { configured: false };
    return (
      <Paper className={classes.section}>
        <Typography variant="h6">חשבון שירות (Service Account)</Typography>
        {account.configured ? (
          <div className={classes.row}>
            <strong style={{ color: "#16a34a" }}>מוגדר</strong>
            <bdi dir="ltr">{account.clientEmail}</bdi>
            <Button size="small" onClick={this.handleCopyEmail}>{copied ? "הועתק" : "העתק"}</Button>
            <span>פרויקט: <bdi dir="ltr">{account.projectId}</bdi></span>
            <span>נשמר: {formatWhen(account.savedAt)} ע"י {account.savedBy || "-"}</span>
          </div>
        ) : (
          <Typography style={{ color: "#6b7280", marginTop: 8 }}>לא מוגדר</Typography>
        )}
        {account.configured && !account.decryptable && (
          <div className={classes.alert} style={{ background: "#fdecea", color: "#b71c1c" }}>
            לא ניתן לפענח את פרטי הגישה — העלה מחדש את קובץ ה-JSON.
          </div>
        )}
        {config && !config.encryptionKeyConfigured && (
          <div className={classes.alert} style={{ background: "#fff8e1", color: "#8a6d3b" }}>
            SETTINGS_ENCRYPTION_KEY לא מוגדר בשרת. בסביבת פרודקשן לא ניתן לשמור פרטי גישה.
          </div>
        )}
        <div className={classes.alert} style={{ background: "#f3f4f6" }}>
          שתף את גיליון ההזמנה עם כתובת חשבון השירות בהרשאת עורך (Editor).
        </div>
        <div className={classes.actions}>
          <input
            id="google-sheets-service-account-file"
            accept="application/json,.json"
            type="file"
            style={{ display: "none" }}
            onChange={this.handleJsonFile}
          />
          <label htmlFor="google-sheets-service-account-file">
            <Button variant="outlined" component="span">העלה קובץ JSON</Button>
          </label>
        </div>
        <TextField
          className={classes.field}
          label="או הדבק כאן את תוכן קובץ ה-JSON"
          value={this.state.serviceAccountJson}
          onChange={this.handleField("serviceAccountJson")}
          multiline
          rows={6}
          fullWidth
          variant="outlined"
          autoComplete="off"
          inputProps={{ dir: "ltr", spellCheck: "false", style: { fontFamily: "monospace", direction: "ltr" } }}
        />
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            onClick={this.handleSaveCredentials}
            disabled={this.state.savingCredentials}
          >
            {this.state.savingCredentials ? <CircularProgress size={20} /> : "שמור פרטי גישה"}
          </Button>
          <Button
            variant="outlined"
            onClick={this.handleDeleteCredentials}
            disabled={this.state.savingCredentials || !account.configured}
          >
            מחק פרטי גישה
          </Button>
          {this.state.credentialsMessage && (
            <Typography variant="caption" className={classes.success}>{this.state.credentialsMessage}</Typography>
          )}
          {this.state.credentialsError && (
            <Typography variant="caption" className={classes.error}>{this.state.credentialsError}</Typography>
          )}
        </div>
      </Paper>
    );
  }

  renderSheet(classes) {
    return (
      <Paper className={classes.section}>
        <Typography variant="h6">גיליון ההזמנה הנוכחי</Typography>
        <TextField
          className={classes.field}
          label="קישור לגיליון Google Sheets"
          value={this.state.sheetUrl}
          onChange={this.handleField("sheetUrl")}
          fullWidth
          variant="outlined"
          inputProps={{ dir: "ltr" }}
        />
        {this.state.sheetUrl ? (
          <div className={classes.info}>
            <a href={this.state.sheetUrl} target="_blank" rel="noopener noreferrer">פתח גיליון</a>
          </div>
        ) : null}
        <TextField
          className={classes.field}
          label='שם לשונית (אופציונלי — ברירת מחדל: לפי הקישור או "Order")'
          value={this.state.sheetTitleOverride}
          onChange={this.handleField("sheetTitleOverride")}
          fullWidth
          variant="outlined"
        />
        <TextField
          className={classes.field}
          label="כותרת עמודת הערות (אופציונלי — ברירת מחדל: העמודה הימנית ביותר)"
          value={this.state.notesColumnHeader}
          onChange={this.handleField("notesColumnHeader")}
          fullWidth
          variant="outlined"
        />
        <TextField
          className={classes.field}
          label="תוויות בלוק הסיכומים (מופרדות בפסיק)"
          value={this.state.totalsMarkerLabels}
          onChange={this.handleField("totalsMarkerLabels")}
          fullWidth
          variant="outlined"
          inputProps={{ dir: "ltr" }}
        />
      </Paper>
    );
  }

  renderSourceMap(classes) {
    return (
      <Paper className={classes.section}>
        <Typography variant="h6">מיפוי ספק → Source</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>טקסט בשם הספק</TableCell>
              <TableCell>ערך בעמודת Source</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {this.state.supplierSourceMap.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <TextField
                    value={row.match}
                    onChange={this.handleMapChange(row.id, "match")}
                    fullWidth
                    inputProps={{ dir: "ltr" }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={row.source}
                    onChange={this.handleMapChange(row.id, "source")}
                    fullWidth
                    inputProps={{ dir: "ltr" }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => this.handleDeleteMapRow(row.id)} aria-label="מחק שורה">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className={classes.actions}>
          <Button onClick={this.handleAddMapRow}>הוסף שורה</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={this.handleSaveSettings}
            disabled={this.state.savingSettings}
          >
            {this.state.savingSettings ? <CircularProgress size={20} /> : "שמור הגדרות"}
          </Button>
          {this.state.settingsMessage && (
            <Typography variant="caption" className={classes.success}>{this.state.settingsMessage}</Typography>
          )}
          {this.state.settingsError && (
            <Typography variant="caption" className={classes.error}>{this.state.settingsError}</Typography>
          )}
        </div>
      </Paper>
    );
  }

  renderTest(classes, unsaved, configured) {
    const { testing, testResult, config } = this.state;
    const layout = testResult && testResult.layout;
    const lastTest = config && config.lastTest;
    return (
      <Paper className={classes.section}>
        <Typography variant="h6">בדיקת חיבור</Typography>
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            onClick={this.handleTest}
            disabled={unsaved || !configured || testing}
          >
            בדוק חיבור והרשאת כתיבה
          </Button>
          {testing && <CircularProgress size={24} />}
        </div>
        {unsaved && (
          <Typography className={classes.info}>שמור את ההגדרות לפני הבדיקה</Typography>
        )}
        {testResult && (testResult.steps || []).map((step) => (
          <div key={step.key} className={classes.step}>
            {stepIcon(step.status)}
            <div>
              <div>{step.label}</div>
              {step.message ? <div className={classes.info} style={{ marginTop: 0 }}>{step.message}</div> : null}
            </div>
          </div>
        ))}
        {layout && (
          <Table size="small" style={{ marginTop: 16 }}>
            <TableBody>
              <TableRow>
                <TableCell>עמודות שזוהו</TableCell>
                <TableCell style={{ direction: "ltr", textAlign: "left" }}>
                  {layout.columns
                    ? Object.keys(layout.columns).map((key) => `${COLUMN_LABELS[key] || key}→${layout.columns[key]}`).join(", ")
                    : ""}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>שורת מוצר אחרונה</TableCell>
                <TableCell><bdi dir="ltr">{layout.lastProductRow}</bdi></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>שורת סיכומים</TableCell>
                <TableCell><bdi dir="ltr">{layout.totalsStartRow || "—"}</bdi></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>השורות יתווספו החל משורה</TableCell>
                <TableCell><bdi dir="ltr">{layout.insertAtRow}</bdi></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>עמודת הערות</TableCell>
                <TableCell><bdi dir="ltr">{layout.notesColumn}</bdi></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>נוסחאות סיכום</TableCell>
                <TableCell>
                  {layout.formulaCoverage
                    ? `${layout.formulaCoverage.auto} יתעדכנו אוטומטית, ${layout.formulaCoverage.fixes} יתוקנו בסנכרון`
                    : ""}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
        {lastTest && (
          <Typography className={classes.info}>
            בדיקה אחרונה: {formatWhen(lastTest.at)} — {lastTest.ok ? "הצליחה" : "נכשלה"}
          </Typography>
        )}
      </Paper>
    );
  }

  render() {
    const { classes } = this.props;
    const { loading, error, config, helpOpen } = this.state;
    if (loading) {
      return (
        <div className={classes.root} style={{ direction: "rtl", textAlign: "center", padding: 40 }}>
          <CircularProgress />
        </div>
      );
    }
    if (error && !config) {
      return (
        <div className={classes.root} style={{ direction: "rtl" }}>
          <Typography className={classes.error}>{error}</Typography>
        </div>
      );
    }
    const account = (config && config.serviceAccount) || {};
    const configured = Boolean(account.configured);
    const unsaved = formSnapshot(this.state) !== this.state.savedSnapshot;
    return (
      <div className={classes.root} style={{ direction: "rtl" }}>
        {this.renderAccount(classes)}
        {this.renderSheet(classes)}
        {this.renderSourceMap(classes)}
        {this.renderTest(classes, unsaved, configured)}
        <Paper className={classes.section}>
          <Button onClick={() => this.setState({ helpOpen: !helpOpen })}>
            איך יוצרים חשבון שירות?
          </Button>
          {helpOpen && (
            <div>
              <ol className={classes.helpList}>
                {HELP_STEPS.map((step) => <li key={step}>{step}</li>)}
              </ol>
              <Typography className={classes.info}>
                עמודת ההערות כוללת את שם הלקוח. אם הגיליון משותף עם הספק, השם יופיע גם אצלו.
                אם מפתח ההצפנה בשרת השתנה או אבד, אי אפשר לפענח את הפרטים השמורים וצריך להעלות את קובץ ה-JSON מחדש.
              </Typography>
            </div>
          )}
        </Paper>
      </div>
    );
  }
}

GoogleSheetsSyncManager.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(GoogleSheetsSyncManager);
